import { useForm, SubmitHandler } from "react-hook-form";
import Button from "../ui/button/Button";
import Label from "../form/Label";
import PasswordInput from "../form/form-elements/PasswordInput";
import useUpdatePassword from "../../hooks/Auth/useUpdatePassword";
import { useToast } from "../../context/ToastContext";

import type { User } from "../../types/userTypes";
import type { UpdatePassword } from "../../types/updatePasswordTypes";

type Props = {
  user: User;
};

const UserChangePasswordCard = ({ user }: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<UpdatePassword>({
    defaultValues: {
      id: user.id!
    }
  });

  const password = watch("password");
  const toast = useToast();

  const { updatePassword } = useUpdatePassword({
    onCompleted: (updatedPassword) => {
      if (updatedPassword.success) {
        reset({
          id: user.id!,
          password: "",
          passwordConfirmation: "",
          currentPassword: ""
        });
        toast.success(updatedPassword.message);
      } else {
        toast.error(updatedPassword.message);
      }
    },
    onError: (err) => {
      console.log(err)
      if (err) return toast.error(String(err)); 
    }
  });

  const onSubmit: SubmitHandler<UpdatePassword> = async (formValues) => {
    await updatePassword(formValues);
  };

  return (
    <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
      <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
        Change Password
      </h4>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <div className="grid grid-cols-1 gap-x-6 gap-y-5">
          <div>
            <Label htmlFor="currentPassword">Current Password</Label>
            <PasswordInput
              placeholder="Enter current password"
              register={register("currentPassword", {
                required: "Current password is required"
              })}
            />
            {errors.currentPassword && (
              <p className="text-sm text-red-600">{errors.currentPassword.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="password">New Password</Label>
            <PasswordInput
              placeholder="Enter new password"
              register={register("password", {
                required: "New password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters"
                },
                pattern: {
                  value: /[0-9]/,
                  message: "Password must contain a number"
                }
              })}
            />
            {errors.password && (
              <p className="text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="passwordConfirmation">Confirm New Password</Label>
            <PasswordInput
              placeholder="Confirm new password"
              register={register("passwordConfirmation", {
                required: "Please confirm password",
                validate: (value) =>
                  value === password || "Passwords do not match"
              })}
            />
            {errors.passwordConfirmation && (
              <p className="text-sm text-red-600">
                {errors.passwordConfirmation.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 px-2 mt-6 lg:justify-end">
          <Button size="sm">
            Update Password
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserChangePasswordCard;
